import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback, useMemo, useRef } from 'react';
import { ErrorScreen } from './ErrorScreen';
import { LoginPage } from './LoginPage';
import { StorageAccessPage } from './StorageAccessPage';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useUrl } from './useUrl';
import { useValidationState, Subjects, LoadingStatus } from './useValidationState';
import { ANALYTICS_CHANNEL } from '../analytics';
var noop = function noop() {};
export var PageLoadValidation = function PageLoadValidation(props) {
  var _useUrl = useUrl(props),
    standaloneConfluenceUrl = _useUrl.standaloneConfluenceUrl,
    embeddedConfluenceUrl = _useUrl.embeddedConfluenceUrl;
  var isEmbeddedUrlOnSameDomain = embeddedConfluenceUrl ? window.location.origin === new URL(embeddedConfluenceUrl).origin : false;
  var unusedIframeRef = useRef(null);
  return isEmbeddedUrlOnSameDomain ? props.children({
    iframeRef: unusedIframeRef,
    handleIframeOnLoad: noop,
    handleContentLoadStarted: noop,
    handleIframeUnload: noop,
    navigationPolicy: props.navigationPolicy
  }) : /*#__PURE__*/React.createElement(CrossDomainErrorValidation, _extends({
    key: embeddedConfluenceUrl || props.contentId,
    standaloneConfluenceUrl: standaloneConfluenceUrl,
    embeddedConfluenceUrl: embeddedConfluenceUrl,
    allowedFeatures: props.allowedFeatures
  }, props));
};
var CrossDomainErrorValidation = function CrossDomainErrorValidation(_ref) {
  var allowedFeatures = _ref.allowedFeatures,
    children = _ref.children,
    contentId = _ref.contentId,
    embeddedConfluenceUrl = _ref.embeddedConfluenceUrl,
    navigationPolicy = _ref.navigationPolicy,
    parentProduct = _ref.parentProduct,
    spaceKey = _ref.spaceKey,
    standaloneConfluenceUrl = _ref.standaloneConfluenceUrl;
  var _useValidationState = useValidationState(),
    state = _useValidationState.state,
    dispatch = _useValidationState.dispatch;
  var iframeLoaded = state.iframeLoaded,
    contentLoadStarted = state.contentLoadStarted,
    shouldAskStorageAccess = state.shouldAskStorageAccess,
    shouldAskToLogin = state.shouldAskToLogin,
    redirectedToLogin = state.redirectedToLogin,
    iframeUnloaded = state.iframeUnloaded;
  var iframeRef = useRef(null);

  /* This ref is specifically used for the purpose of recoding whether or not a delayed dispatch of the iframe occured.
  	This recorded value is later used to fire analytics if contentLoadStarted arrives after the delayed dispatch, and reset
  	once the event has fired or if the users needs to go through login flow.
  */
  var iframeLoadedWithDelayRef = useRef(false);
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  var isLoginFlowDisabled = useMemo(function () {
    var _allowedFeatures$incl;
    return (_allowedFeatures$incl = allowedFeatures === null || allowedFeatures === void 0 ? void 0 : allowedFeatures.includes('disable-login-flow')) !== null && _allowedFeatures$incl !== void 0 ? _allowedFeatures$incl : false;
  }, [allowedFeatures]);
  var reloadEP = useCallback(function () {
    dispatch({
      subject: Subjects.CONTENT_LOAD,
      status: LoadingStatus.RELOAD_EP
    });
  }, [dispatch]);
  var failToLoad = useMemo(function () {
    return iframeLoaded && !contentLoadStarted || (
    /*
       If anonymous access is permitted globally but restricted on a content's parent space,
       the iframe will attempt a login redirect which we intercept.
       To avoid an error in the iframe, we need to wait for the iframe window to unload
       before unmouting the Iframe component.
     */
    iframeUnloaded && redirectedToLogin);
  }, [contentLoadStarted, iframeLoaded, iframeUnloaded, redirectedToLogin]);
  var pageNavigationPolicy = useMemo(function () {
    return {
      navigate: function navigate(url, modifiers, defaultNavigate) {
        // modifiy the navigationPolicy to intercept login redirects
        // so that login can be handled outside of the iframe
        if (url.includes('/login?application=confluence')) {
          dispatch({
            subject: Subjects.CONTENT_LOAD,
            status: LoadingStatus.REDIRECTED_TO_LOGIN
          });
          return;
        }
        return navigationPolicy !== null && navigationPolicy !== void 0 && navigationPolicy.navigate ? navigationPolicy.navigate(url, modifiers, defaultNavigate) : defaultNavigate(url, modifiers);
      },
      shimUrl: navigationPolicy === null || navigationPolicy === void 0 ? void 0 : navigationPolicy.shimUrl,
      domainNamesToReplace: navigationPolicy === null || navigationPolicy === void 0 ? void 0 : navigationPolicy.domainNamesToReplace,
      spaceKeyFilter: navigationPolicy === null || navigationPolicy === void 0 ? void 0 : navigationPolicy.spaceKeyFilter
    };
  }, [navigationPolicy, dispatch]);
  var handleIframeOnLoad = useCallback(function () {
    var dispatchNow = function dispatchNow() {
      dispatch({
        subject: Subjects.CONTENT_LOAD,
        status: LoadingStatus.IFRAME_LOADED
      });
    };
    var totalNumberOfTries = 2;
    var iframeOfDispatch = iframeRef.current;
    var iframeSrcOfDispatch = iframeOfDispatch === null || iframeOfDispatch === void 0 ? void 0 : iframeOfDispatch.src;
    var tries = 0;
    var _dispatchWithDelay = function dispatchWithDelay() {
      /* We verify whether the iframeRef is current, as our intent is to invoke this only for the Iframe component created within the Content component (embedded-confluence-common/src/content/Content.tsx).
      It's important to note that the same Iframe component is also used by the StorageAccessPage component; however, we do not need to perform that check in this context.
      Additionally, the src of the iframe may change during transitions between view/edit modes or other redirects. Therefore, we must ensure that our invocation is limited to the current src of the iframe.
      */
      if (iframeRef.current !== iframeOfDispatch || (iframeOfDispatch === null || iframeOfDispatch === void 0 ? void 0 : iframeOfDispatch.src) !== iframeSrcOfDispatch) {
        return;
      }
      ++tries;
      if (contentLoadStarted || tries > totalNumberOfTries) {
        // If content load arrived after a timeout, failure to load was prevented by the delay
        if (contentLoadStarted && iframeLoadedWithDelayRef.current) {
          createAnalyticsEvent({
            action: 'delayedDispatch',
            actionSubject: 'iframeOnLoad',
            actionSubjectId: 'crossDomainErrorValidation',
            source: 'pageLoadValidation',
            eventType: 'track'
          }).fire(ANALYTICS_CHANNEL);

          //reset the ref for now. If the delayed dispatch is triggered again, the event will get fired too
          iframeLoadedWithDelayRef.current = false;
        }
        dispatchNow();
      } else {
        /* This determines whether to fire a track analytics event
        to log instances where iframe load is delayed to wait for the confluence content to load.*/
        if (!iframeLoadedWithDelayRef.current) {
          iframeLoadedWithDelayRef.current = true;
        }
        setTimeout(_dispatchWithDelay, 1000);
      }
    };
    _dispatchWithDelay();
  }, [contentLoadStarted, createAnalyticsEvent, dispatch]);
  var handleContentLoadStarted = useCallback(function () {
    dispatch({
      subject: Subjects.CONTENT_LOAD,
      status: LoadingStatus.CONTENT_LOAD_STARTED
    });
  }, [dispatch]);
  var handleIframeUnload = useCallback(function () {
    dispatch({
      subject: Subjects.CONTENT_LOAD,
      status: LoadingStatus.IFRAME_UNLOADED
    });
  }, [dispatch]);
  if (!failToLoad) {
    return children({
      iframeRef: iframeRef,
      handleIframeOnLoad: handleIframeOnLoad,
      handleContentLoadStarted: handleContentLoadStarted,
      handleIframeUnload: handleIframeUnload,
      navigationPolicy: pageNavigationPolicy,
      redirectedToLogin: redirectedToLogin
    });
  }
  if (!isLoginFlowDisabled) {
    if (shouldAskStorageAccess) {
      return /*#__PURE__*/React.createElement(StorageAccessPage, {
        url: embeddedConfluenceUrl,
        contentId: contentId,
        spaceKey: spaceKey,
        parentProduct: parentProduct
      });
    }
    if (shouldAskToLogin) {
      return /*#__PURE__*/React.createElement(LoginPage, {
        contentId: contentId,
        reloadEP: reloadEP,
        spaceKey: spaceKey,
        url: embeddedConfluenceUrl
      });
    }
  }
  return /*#__PURE__*/React.createElement(ErrorScreen, {
    standaloneConfluenceUrl: standaloneConfluenceUrl
  });
};